/**
 * Масштабы «Плана»: Неделя · Цикл · Сезон (MOBILE_NAV_ARCHITECTURE.md раздел 2).
 *
 * Три масштаба ОДНОГО объекта, а не три раздела: цикл и сезон смотрят на те
 * же тренировки и фазы, что и неделя, только с другой высоты. Логика окна
 * цикла и сборки недельных столбиков — чистые функции: проверяются без
 * рендера и не зависят от часового пояса (ISO-математика через Date.UTC,
 * как в planUtils).
 */

import { itemLoad, type LoadItem } from "./planUtils";

export type PlanScale = "week" | "cycle" | "season";
export const PLAN_SCALE_KEY = "storica.plan.scale.v1";

export function isPlanScale(v: unknown): v is PlanScale {
  return v === "week" || v === "cycle" || v === "season";
}

export interface PhaseWindowSpec {
  name: string;
  /** ISO yyyy-mm-dd, включительно. */
  start: string;
  end: string;
}

export interface CycleWeek {
  mondayISO: string;
  planned: number;
  actual: number;
  /** Имя фазы из БД, в которую попадает понедельник недели. */
  phase: string | null;
  isCurrent: boolean;
  hasRace: boolean;
  hasKey: boolean;
}

const WEEK_MS = 7 * 86_400_000;

function utcOf(iso: string): number {
  const [y, m, d] = iso.split("-").map(Number);
  return Date.UTC(y, m - 1, d);
}

function isoOfUtc(ts: number): string {
  const d = new Date(ts);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
}

export function addDaysISO(iso: string, days: number): string {
  return isoOfUtc(utcOf(iso) + days * 86_400_000);
}

/** Понедельник недели, в которую попадает дата (ISO, без TZ-сюрпризов). */
export function mondayISOOf(iso: string): string {
  const ts = utcOf(iso);
  const jsDay = new Date(ts).getUTCDay(); // 0=Sun..6=Sat
  const diff = jsDay === 0 ? -6 : 1 - jsDay;
  return isoOfUtc(ts + diff * 86_400_000);
}

/**
 * Окно цикла: активная фаза, если сегодня внутри какой-то из фаз, иначе
 * шесть недель вокруг текущей (3 назад, текущая, 2 вперёд). Длинная фаза
 * обрезается до 8 недель вокруг текущей — на телефоне столбики уже, чем
 * палец, читать 16 недель подряд невозможно.
 */
export const CYCLE_MAX_WEEKS = 8;

export function resolveCycleWindow(
  phases: readonly PhaseWindowSpec[],
  todayISO: string,
): { firstMondayISO: string; weeks: number; phaseName: string | null } {
  const todayMonday = mondayISOOf(todayISO);
  const active = phases.find((p) => todayISO >= p.start && todayISO <= p.end);
  if (!active) {
    return { firstMondayISO: addDaysISO(todayMonday, -21), weeks: 6, phaseName: null };
  }
  const firstMonday = mondayISOOf(active.start);
  const lastMonday = mondayISOOf(active.end);
  const weeks = Math.round((utcOf(lastMonday) - utcOf(firstMonday)) / WEEK_MS) + 1;
  if (weeks <= CYCLE_MAX_WEEKS) {
    return { firstMondayISO: firstMonday, weeks, phaseName: active.name };
  }
  // Фаза длиннее экрана: окно вокруг текущей недели, не выходя за фазу.
  let start = addDaysISO(todayMonday, -21);
  if (start < firstMonday) start = firstMonday;
  let end = addDaysISO(start, (CYCLE_MAX_WEEKS - 1) * 7);
  if (end > lastMonday) {
    end = lastMonday;
    start = addDaysISO(end, -(CYCLE_MAX_WEEKS - 1) * 7);
    if (start < firstMonday) start = firstMonday;
  }
  const w = Math.round((utcOf(end) - utcOf(start)) / WEEK_MS) + 1;
  return { firstMondayISO: start, weeks: w, phaseName: active.name };
}

/** Единица столбиков цикла: TSS, а когда планового TSS нет — минуты. */
export type CycleUnit = "tss" | "min";

export interface CycleWeeksResult {
  weeks: CycleWeek[];
  unit: CycleUnit;
}

/**
 * Недельные столбики цикла из сырых тренировок. Плановая и фактическая
 * нагрузка считаются раздельно: план — что назначено, факт — что сделано
 * (actual_load приоритетнее planned_load только у выполненных — это
 * решает itemLoad из planUtils; здесь план = planned_load всегда, чтобы
 * столбик «сколько задумано» не менялся задним числом).
 *
 * У импортных планов planned_load пуст у всех (v211 раздел 1.8) — цикл целиком
 * показывал «план 0» при полном календаре. Если планового TSS нет вовсе,
 * а плановые минуты есть, обе серии переключаются на минуты — единица
 * одна на обе серии, ничего не выдумываем.
 */
export function buildCycleWeeks(
  items: ReadonlyArray<LoadItem & { is_key_workout?: boolean }>,
  phases: readonly PhaseWindowSpec[],
  firstMondayISO: string,
  weeks: number,
  todayISO: string,
  raceDateISO: string | null,
): CycleWeeksResult {
  const start = utcOf(firstMondayISO);
  const todayMonday = mondayISOOf(todayISO);
  const out: CycleWeek[] = Array.from({ length: weeks }, (_, i) => {
    const mondayISO = isoOfUtc(start + i * WEEK_MS);
    const sundayISO = addDaysISO(mondayISO, 6);
    return {
      mondayISO,
      planned: 0,
      actual: 0,
      phase: phases.find((p) => mondayISO >= mondayISOOf(p.start) && mondayISO <= p.end)?.name ?? null,
      isCurrent: mondayISO === todayMonday,
      hasRace: raceDateISO != null && raceDateISO >= mondayISO && raceDateISO <= sundayISO,
      hasKey: false,
    };
  });
  const plannedMin = new Array<number>(weeks).fill(0);
  const actualMin = new Array<number>(weeks).fill(0);
  for (const w of items) {
    const idx = Math.floor((utcOf(w.scheduled_date) - start) / WEEK_MS);
    if (idx < 0 || idx >= weeks) continue;
    out[idx].planned += w.planned_load ?? 0;
    plannedMin[idx] += w.planned_duration_minutes ?? 0;
    const done = w.status === "completed" || w.status === "done";
    if (done) {
      out[idx].actual += itemLoad(w) ?? 0;
      actualMin[idx] += w.actual_duration_minutes ?? w.planned_duration_minutes ?? 0;
    }
    if (w.is_key_workout) out[idx].hasKey = true;
  }
  const totalPlannedTss = out.reduce((s, w) => s + w.planned, 0);
  const totalPlannedMin = plannedMin.reduce((s, v) => s + v, 0);
  const unit: CycleUnit = totalPlannedTss <= 0 && totalPlannedMin > 0 ? "min" : "tss";
  if (unit === "min") {
    out.forEach((w, i) => {
      w.planned = plannedMin[i];
      w.actual = actualMin[i];
    });
  }
  for (const w of out) {
    w.planned = Math.round(w.planned);
    w.actual = Math.round(w.actual);
  }
  return { weeks: out, unit };
}
